import request from 'api/requestUtils'
import { templates } from '@luxuryescapes/lib-uri-templates'
import createBatchedPromise from './createBatchedPromise'
import type { paths } from '@luxuryescapes/contract-svc-reservation/index'

export function createSurchargeMarginKey(ratePlanId: string, startDate: string, endDate: string) {
  return `${ratePlanId}_${startDate}_${endDate}`
}

type SurchargeMarginBatchCalculateServerResponse = {
  status: 200 | 201 | 204
  message?: string
  result: Array<{
    /** Format: uuid */
    roomRateId: string
    /** Format: ISO8601 */
    checkIn: string
    /** Format: ISO8601 */
    checkOut: string
    marginAud: number
  }>
  count?: number
}
type SurchargeMarginBatchCalculateServerRequestPayload = paths['/api/rate-plans/surcharge-dates/margin']['post']['requestBody']['content']['application/json']

export const batchFetchSurchargeMargin = createBatchedPromise({
  argsReducerFn: (
    accumulatedArgs: Record<string, { startDate: string, endDate: string }>,
    singleArg: [roomRateId: string, surchargeDateBlockInfo: { startDate: string, endDate: string }],
  ) => {
    const [ratePlanId, surchargeDateBlockInfo] = singleArg
    return {
      ...accumulatedArgs,
      [ratePlanId]: { ...surchargeDateBlockInfo },
    }
  },
  batchRequestFn: (batchedArgs) => {
    return requestPostBatchSurchargeMargin(batchedArgs)
  },
  singleValueFn: (singleArg, batchedResponse): App.SurchargeMargin => {
    const roomRateId = singleArg[0]
    const checkIn = singleArg[1].startDate
    const checkOut = singleArg[1].endDate
    const singleResponse = batchedResponse.find((response) => response.roomRateId === roomRateId && response.checkIn === checkIn && response.checkOut === checkOut)
    return {
      status: 'success',
      roomRateId,
      checkIn,
      checkOut,
      marginAud: singleResponse?.marginAud ?? 0,
    }
  },
})

async function requestPostBatchSurchargeMargin(
  payload: Record<string, { startDate: string, endDate: string }>,
) {
  try {
    const response = await request.post<SurchargeMarginBatchCalculateServerResponse, SurchargeMarginBatchCalculateServerRequestPayload>(
      '/api/rate-plans/surcharge-dates/margin',
      payload,
      { credentials: 'include' },
    )

    return response.result
  } catch(e) {
    return Promise.reject(e)
  }
}

function tourDateMap(date: any): App.TourDate {
  return {
    startDate: date.start_date,
    endDate: date.end_date,
    monthGroup: date.month_group,
    totalAvailable: date.total_available,
    guaranteedDeparture: date.guaranteedDeparture,
  }
}

export function tourEnquiry(
  tourId: string,
  tourOptionId: string,
  numberOfDays: number,
  noOfAdults: number,
) {
  return request.get<App.ApiResponse<Array<any>>>(templates.reservation.tour_enquiry.expand({
    tour_id: tourId,
    id: tourOptionId,
    days: numberOfDays,
    timezone_offset: timezoneOffset(),
    no_of_adults: noOfAdults,
  }))
    .then(data => data.result.map(tourDateMap))
}

function timezoneOffset() {
  return new Date().getTimezoneOffset() / 60 * -1
}
